import React, { useState, useEffect } from 'react';
import AdminNav from '../../components/admin/AdminNav';
import ExcelTable from '../../components/admin/ExcelTable';
import { formatDate } from '../../utils/dateHelper';

const TrashView = ({ backendUrl }) => {
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  // Track AdminNav mode
  const [navMode, setNavMode] = useState(() => {
    return localStorage.getItem('adminNavMode') || (window.innerWidth < 1024 ? 'peek' : 'expanded');
  });

  // Listen for AdminNav toggle events
  useEffect(() => {
    const handleNavToggle = (e) => {
      if (e.detail.mode) {
        setNavMode(e.detail.mode);
      } else {
        setNavMode(e.detail.isCollapsed ? 'collapsed' : 'expanded');
      }
    };
    window.addEventListener('adminNavToggle', handleNavToggle);
    return () => window.removeEventListener('adminNavToggle', handleNavToggle);
  }, []);

  useEffect(() => {
    fetchTrash();
  }, []);

  const fetchTrash = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/trash`, {
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      const data = await response.json();

      if (data.success) {
        setInspections(data.inspections || []);
      }
    } catch (error) {
      console.error('Error fetching trash:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (inspectionIds) => {
    const confirmed = window.confirm(
      `Restore ${inspectionIds.length} inspection(s) from trash?`
    );

    if (!confirmed) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/trash/restore`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ inspection_ids: inspectionIds })
      });

      const data = await response.json();

      if (data.success) {
        alert(`✅ ${data.restored_count} inspection(s) restored`);
        fetchTrash();
      } else {
        alert(`❌ Failed to restore: ${data.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error restoring:', error);
      alert(`❌ Failed to restore: ${error.message}`);
    }
  };

  const handleEmptyTrash = async () => {
    const confirmed = window.confirm(
      `⚠️ PERMANENT DELETE!\n\nEmpty trash and permanently delete ${inspections.length} inspection(s)?\n\nThis CANNOT be undone!`
    );

    if (!confirmed) return;

    // Double confirmation for safety
    const doubleConfirm = window.confirm(
      'Are you ABSOLUTELY SURE? This will permanently delete all items in trash.'
    );

    if (!doubleConfirm) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/trash/empty`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      const data = await response.json();

      if (data.success) {
        alert(`✅ ${data.deleted_count} inspection(s) permanently deleted`);
        fetchTrash();
      } else {
        alert(`❌ Failed to empty trash`);
      }
    } catch (error) {
      console.error('Error emptying trash:', error);
      alert(`❌ Failed to empty trash: ${error.message}`);
    }
  };

  const columns = [
    {
      key: 'thumbnail_url',
      label: 'Image',
      sortable: false,
      render: (url, row) => (
        <img
          src={url}
          alt="Tag"
          className="w-16 h-16 object-cover rounded cursor-pointer hover:ring-2 hover:ring-red-500"
          onClick={() => setSelectedImage(row.image_url)}
          loading="lazy"
        />
      )
    },
    {
      key: 'all_dates',
      label: 'Dates',
      sortable: false,
      render: (val, row) => (
        <div className="flex flex-col gap-1 text-xs">
          <div className="px-1 py-0.5">
            <span className="text-white/50">Captured:</span>{' '}
            <span className="text-white/80">
              {row.capture_date ? formatDate(row.capture_date, true) : '—'}
            </span>
          </div>
          <div className="px-1 py-0.5">
            <span className="text-white/50">Deleted:</span>{' '}
            <span className="text-red-400">
              {row.deleted_at ? formatDate(row.deleted_at, true) : '—'}
            </span>
          </div>
          <div className="px-1 py-0.5 text-white/40 text-[10px]">
            Original: {row.original_status || 'unknown'}
          </div>
        </div>
      )
    },
    {
      key: 'site_location',
      label: 'Site Location',
      sortable: false,
      render: (val, row) => (
        <div className="flex flex-col gap-0.5 text-xs">
          <div className="font-semibold text-white/90">{row.business_name || '—'}</div>
          <div className="text-white/70">{row.site_street_address || '—'}</div>
          <div className="text-white/70">
            {[row.site_city, row.site_state, row.site_zip].filter(Boolean).join(', ') || '—'}
          </div>
        </div>
      )
    },
    {
      key: 'assigned_branch',
      label: 'Nearest Branch',
      sortable: true,
      render: (val) => val?.name || '—'
    },
    {
      key: 'submitted_by',
      label: 'Submitted By',
      sortable: true
    }
  ];

  const getNavOffset = () => {
    switch (navMode) {
      case 'peek': return 'ml-4 w-[calc(100%-1rem)]';
      case 'collapsed': return 'ml-20 w-[calc(100%-5rem)]';
      default: return 'ml-64 w-[calc(100%-16rem)]';
    }
  };

  return (
    <div className={`h-screen overflow-hidden bg-gradient-to-br from-[#00314F] via-[#003d5c] to-[#00314F] text-white ${getNavOffset()} overflow-x-hidden`}>
      <AdminNav />

      <div className="pt-6 px-6 h-full overflow-hidden flex flex-col max-w-full">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white leading-tight">🗑️ Trash</h1>
            <p className="text-white/60 text-xs sm:text-sm">Deleted inspections ({inspections.length} items)</p>
          </div>
          {inspections.length > 0 && (
            <button
              onClick={handleEmptyTrash}
              className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors font-medium border border-red-500/30"
            >
              🗑️ Empty Trash ({inspections.length})
            </button>
          )}
        </div>

        <div className="flex-1 bg-[var(--pbfs-navy)]/40 backdrop-blur-sm rounded-2xl border border-white/10 shadow-2xl overflow-hidden flex flex-col">
          <ExcelTable
            data={inspections}
            columns={columns}
            expandableColumns={[]}
            onDelete={handleRestore} // Restore button (reuse delete prop)
            currentStatusFilter="deleted"
            loading={loading}
            editingCell={null}
            setEditingCell={() => { }}
            editValue=""
            setEditValue={() => { }}
            onCellSave={() => { }}
          />
        </div>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setSelectedImage(null)}>
          <img src={selectedImage} alt="Full size" className="max-w-full max-h-full rounded-lg shadow-2xl" />
        </div>
      )}
    </div>
  );
};

export default TrashView;

